import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { LoyaltyBrandAPIPromiseClient } from "../../../gen/witbooking/backoffice/loyalty/brand/v1/loyalty_brand_api_grpc_web_pb";
import {
  LoyaltyBrand,
  LoyaltyBrandEmailAddress,
  LoyaltyBrandEmailAddressType,
  LoyaltyBrandConfiguration,
  LoyaltyBrandTranslation,
  UpdateLoyaltyBrandRequest,
  WidgetConfiguration,
} from "../../../gen/witbooking/backoffice/loyalty/brand/v1/loyalty_brand_api_pb";
import { BrandAPIPromiseClient } from "../../../gen/witbooking/loyalty/brand/v1/brand_api_grpc_web_pb";
import { GetBrandByTickerRequest } from "../../../gen/witbooking/loyalty/brand/v1/brand_api_pb";
import { ACCESS_DENIED } from "../../../lib/auth";
import { useGrpcClient, useInternalGrpcCall } from "../../../lib/grpc";
import { mapFormDataToWidgetConfiguration } from "../mappers";
import { BrandFormData, OperationError } from "../types";

// Fallback values if brand fetch fails
const DEFAULT_BRAND_CURRENCY = "EUR";
const DEFAULT_BRAND_LOCALE = "es";

interface UseBrandSaveProps {
  selectedBrand?: LoyaltyBrand;
  onSuccess?: (usedByList: string[], managedByList: string[]) => void | Promise<void>;
}

interface UseBrandSaveReturn {
  loading: boolean;
  error?: OperationError;
  saveSuccess: boolean;
  onSubmit: (data: BrandFormData) => Promise<void>;
  setError: (error?: OperationError) => void;
  setSaveSuccess: (success: boolean) => void;
}

export const useBrandSave = ({
  selectedBrand,
  onSuccess,
}: UseBrandSaveProps): UseBrandSaveReturn => {
  const queryClient = useQueryClient();
  const grpcClient = useGrpcClient(LoyaltyBrandAPIPromiseClient);
  const brandApiClient = useGrpcClient(BrandAPIPromiseClient);
  const updateLoyaltyBrand = useInternalGrpcCall(grpcClient, grpcClient.updateLoyaltyBrand);
  const getBrandByTicker = useInternalGrpcCall(brandApiClient, brandApiClient.getBrandByTicker);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<OperationError | undefined>(undefined);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);

  const onSubmit = useCallback(
    async (data: BrandFormData) => {
      if (!selectedBrand) {
        setError({ message: "No brand selected" });
        return;
      }

      setLoading(true);
      setError(undefined);
      setSaveSuccess(false);

      try {
        const brand = new LoyaltyBrand();
        brand.setId(selectedBrand.getId());
        brand.setTicker(selectedBrand.getTicker());

        // Build the translations list: deduplicate by locale, keep only entries with a non-empty name
        const translationsMap = new Map<string, typeof data.translationsList[0]>();
        data.translationsList.forEach(trans => {
          if (trans.name && trans.name.trim() !== "") {
            translationsMap.set(trans.locale, trans);
          }
        });

        const translationsList = Array.from(translationsMap.values()).map(trans => {
          const translation = new LoyaltyBrandTranslation();
          translation.setLocale(trans.locale);
          translation.setName(trans.name!.trim().replace(/\s+/g, " "));
          translation.setDescription(
            trans.description && trans.description.trim() !== ""
              ? trans.description.trim()
              : "brand description"
          );
          return translation;
        });

        brand.setTranslationsList(translationsList);

        brand.setManagedByList(data.managedByOptions.map(option => option.value));
        brand.setUsedByList(data.usedByOptions.map(option => option.value));

        const emailAddresses: LoyaltyBrandEmailAddress[] = [];

        const receiverEmail = new LoyaltyBrandEmailAddress();
        receiverEmail.setType(LoyaltyBrandEmailAddressType.RECEIVER);
        receiverEmail.setValue(data.emailReceiver);
        emailAddresses.push(receiverEmail);

        const senderEmail = new LoyaltyBrandEmailAddress();
        senderEmail.setType(LoyaltyBrandEmailAddressType.SENDER);
        senderEmail.setValue(data.emailSender);
        emailAddresses.push(senderEmail);

        brand.setEmailAddressesList(emailAddresses);

        const configuration = new LoyaltyBrandConfiguration();
        configuration.setEnableemailchange(data.enableEmailChange);
        configuration.setSendwelcomeemail(data.sendWelcomeEmail);
        configuration.setUseexternaltier(data.useExternalTier);
        configuration.setUseexternalauthsystem(data.useExternalAuthSystem);
        brand.setConfiguration(configuration);

        // Widget configuration
        const widgetConfig = mapFormDataToWidgetConfiguration(data);
        if (widgetConfig) {
          brand.setWidgetConfiguration(widgetConfig);
        } else {
          // Explicitly send an empty WidgetConfiguration so the backend clears
          // any previously stored widget data (avoids protobuf merge keeping old values)
          const emptyWidgetConfig = new WidgetConfiguration();
          emptyWidgetConfig.setUrl("");
          emptyWidgetConfig.setTierAgencyId("");
          emptyWidgetConfig.setTierCompanyId("");
          brand.setWidgetConfiguration(emptyWidgetConfig);
        }

        // Preserve existing fields that are not managed by the form
        const transactionLimit = selectedBrand.getTransactionExpirationLimit();
        if (transactionLimit) {
          brand.setTransactionExpirationLimit(transactionLimit);
        }

        const relatedDomains = selectedBrand.getRelatedDomainsList();
        if (relatedDomains && relatedDomains.length > 0) {
          brand.setRelatedDomainsList(relatedDomains);
        }

        const externalSystemId = selectedBrand.getExternalSystemId();
        if (externalSystemId) {
          brand.setExternalSystemId(externalSystemId);
        }

        const mainDomain = selectedBrand.getMainDomain();
        if (mainDomain) {
          brand.setMainDomain(mainDomain);
        }

        const authServiceDomain = selectedBrand.getAuthorizationServiceDomain();
        if (authServiceDomain) {
          brand.setAuthorizationServiceDomain(authServiceDomain);
        }

        const bookAndJoinType = selectedBrand.getBookAndJoinType();
        if (bookAndJoinType) {
          brand.setBookAndJoinType(bookAndJoinType);
        }

        const registrationTextType = selectedBrand.getRegistrationTextType();
        if (registrationTextType) {
          brand.setRegistrationTextType(registrationTextType);
        }

        // IMPORTANT: Always set policy fields after copying existing fields to ensure they are overridden
        const commPolicy = data.communicationPolicy?.trim() || "";
        const privPolicy = data.privacyPolicy?.trim() || "";

        if (commPolicy) {
          brand.setCommunicationpolicyurl(commPolicy);
        }

        if (privPolicy) {
          brand.setPrivacypolicyurl(privPolicy);
        }

        // Fetch brand defaults (currency and locale) from the main Brand API
        let defaultCurrency = DEFAULT_BRAND_CURRENCY;
        let defaultLocale = DEFAULT_BRAND_LOCALE;

        try {
          const brandRequest = new GetBrandByTickerRequest();
          brandRequest.setTicker(selectedBrand.getTicker());
          const brandResponse = await getBrandByTicker(brandRequest);
          const mainBrand = brandResponse.getBrand();

          if (mainBrand) {
            defaultCurrency = mainBrand.getDefaultCurrency() || DEFAULT_BRAND_CURRENCY;
            defaultLocale = mainBrand.getDefaultLocale() || DEFAULT_BRAND_LOCALE;
          }
        } catch (e) {
          // Fallback values are already set; proceed with the update
        }

        const request = new UpdateLoyaltyBrandRequest();
        request.setBrand(brand);
        request.setDefaultCurrency(defaultCurrency);
        request.setDefaultLocale(defaultLocale);

        await updateLoyaltyBrand(request);

        const usedByList = data.usedByOptions.map(option => option.value);
        const managedByList = data.managedByOptions.map(option => option.value);

        if (onSuccess) {
          await onSuccess(usedByList, managedByList);
        }

        // Wait briefly before refetching to allow backend propagation
        await new Promise(resolve => setTimeout(resolve, 2000));

        await queryClient.invalidateQueries({
          queryKey: ["loyaltyBrand", selectedBrand.getTicker()],
          exact: true,
        });
        await queryClient.refetchQueries({
          queryKey: ["loyaltyBrand", selectedBrand.getTicker()],
          exact: true,
        });

        setSaveSuccess(true);
      } catch (e) {
        setSaveSuccess(false);

        if (e === ACCESS_DENIED) {
          setError({ message: "Access denied" });
        } else {
          setError({ message: e instanceof Error ? e.message : "Error updating brand" });
        }
      } finally {
        setLoading(false);
      }
    },
    [selectedBrand, updateLoyaltyBrand, getBrandByTicker, queryClient, onSuccess]
  );
  return {
    loading,
    error,
    saveSuccess,
    onSubmit,
    setError,
    setSaveSuccess,
  };
};
